import { create } from 'zustand';
import { apiFetch } from '@/lib/api';
import { AuthService } from '../services/AuthService';
import type { User } from '@/types/user';

type AuthState = {
  user: User | null;
  isLoading: boolean;
  error: string | null;
};

type AuthActions = {
  // register: (email: string, password: string, fullName: string, orgName: string) => Promise<void>; // Registration disabled
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: (skipRedirect?: boolean) => Promise<void>;
};

const svc = new AuthService();

// Deduplicate in-flight requests to /api/auth/me (e.g., StrictMode double effects)
let ongoingMeRequest: Promise<User> | null = null;

export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  user: null,
  isLoading: false,
  error: null,

  // Registration method removed - account creation is disabled

  login: async (email, password, rememberMe) => {
    try {
      set({ isLoading: true, error: null });
      const me = await svc.login({ email, password, rememberMe });
      set({ user: me, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Login failed', isLoading: false });
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true, error: null });
      await svc.logout();
      set({ user: null, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Logout failed', isLoading: false });
    }
  },

  fetchMe: async (skipRedirect = false) => {
    // If a request is already in-flight, await it instead of issuing another
    if (ongoingMeRequest) {
      try {
        await ongoingMeRequest;
      } finally {
        set({ isLoading: false });
      }
      return;
    }

    try {
      set({ isLoading: true, error: null });
      ongoingMeRequest = svc.me(skipRedirect);
      const me = await ongoingMeRequest;
      set({ user: me, isLoading: false });
    } catch (e) {
      set({ user: null, isLoading: false });
    } finally {
      ongoingMeRequest = null;
    }
  },
}));


